import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useAppSelector, useAppDispatch } from "../../../common/hooks/hooks";
import { closeSettingsPopup } from "./settingsPopupSlice";
import { openGlobalModal } from "../../../common/features/globalModal/globalModalSlice";
import LanguageSwitcher from "../../../common/features/language/components/LanguageSwitcher";
import CurrencySwitcher from "../../../common/features/currency/components/CurrencySwitcher";
import Icon from "../../../common/components/Icon/Icon";

const SettingsPopupBodySkeleton: React.FC = () => (
  <div className="settings-popup__body">
    <div className="settings-option">
      <Skeleton width={80} height={20} />
      <Skeleton width={100} height={28} />
    </div>
    <div className="settings-option">
      <Skeleton width={80} height={20} />
      <Skeleton width={100} height={28} />
    </div>
    <div className="settings-option">
      <Skeleton width={60} height={20} />
      <Skeleton width={56} height={28} />
    </div>
  </div>
);

const SettingsPopup: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.settingsPopup.isOpen);

  // --- Check if multi-currency is active ---
  const supportedCurrencies = useAppSelector(
    (state) => state.currency.supportedCurrencies,
  );
  const hasMultipleCurrencies = supportedCurrencies.length > 1;

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const isToggler = (target as HTMLElement).closest(
        "[data-settings-toggle]",
      );

      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        !isToggler
      ) {
        dispatch(closeSettingsPopup());
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch(closeSettingsPopup());
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, dispatch]);

  const handleOpenCookiePreferences = () => {
    dispatch(closeSettingsPopup());
    dispatch(
      openGlobalModal({
        contentKey: "cookiePreferences",
        title: t("cookieConsent.preferencesTitle", "Cookie Preferences"),
      }),
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={popupRef}
      id="settings-popup"
      className={`settings-popup ${isOpen ? "is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-popup-title"
      hidden={!isOpen}
    >
      <div className="settings-popup__header">
        <h2 id="settings-popup-title" className="settings-popup__title">
          {areTranslationsReady ? (
            t("settingsPopup.title", "Settings")
          ) : (
            <Skeleton width={80} />
          )}
        </h2>
        <button
          className="settings-popup__close"
          aria-label="Close settings"
          onClick={() => dispatch(closeSettingsPopup())}
        >
          <Icon iconName="x-mark" className="icon--stroke-thickest" />
        </button>
      </div>
      {areTranslationsReady ? (
        <div className="settings-popup__body">
          <div className="settings-option">
            <label>{t("settingsPopup.languageLabel", "Language:")}</label>
            <LanguageSwitcher />
          </div>

          {/* Conditionally hide the entire row if Aelia is disabled */}
          {hasMultipleCurrencies && (
            <div className="settings-option">
              <label>{t("settingsPopup.currencyLabel", "Currency:")}</label>
              <CurrencySwitcher />
            </div>
          )}

          <div className="settings-option">
            <label>{t("settingsPopup.privacyLabel", "Cookie Settings:")}</label>
            <button
              className="settings-popup__action-btn"
              onClick={handleOpenCookiePreferences}
            >
              <Icon iconName="cookie" className="icon--stroke-thick" />
              <span>
                {t("settingsPopup.cookiePreferences", "Cookie Preferences")}
              </span>
            </button>
          </div>
        </div>
      ) : (
        <SettingsPopupBodySkeleton />
      )}
    </div>
  );
};

export default SettingsPopup;
